import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getLang } from 'shared/store/selectors/lang.selector';
import { getWorkspaces } from 'shared/store/selectors/workspace.selector';

/**
 * Shared hook for mirror-related actions used by both
 * KnowledgeCategory and KnowledgeSection components.
 *
 * @param {{
 *   mirroredFromSourceLink: string | null,
 *   mirroredFromWorkspaceId?: string | null,
 * }} params
 */
export function useMirrorActions({ mirroredFromSourceLink, mirroredFromWorkspaceId }) {
  const navigate = useNavigate();
  const mirrorLang = useSelector(getLang('KNOWLEDGE_CATEGORY'));
  const workspaces = useSelector(getWorkspaces);

  const canAccessSourceWorkspace = useMemo(() => {
    if (!mirroredFromWorkspaceId) return true;
    return (workspaces ?? []).some(w => String(w?.id) === String(mirroredFromWorkspaceId));
  }, [workspaces, mirroredFromWorkspaceId]);

  let sourceHref = null;
  if (mirroredFromSourceLink) {
    try {
      const url = new URL(mirroredFromSourceLink);
      sourceHref = `${url.pathname}${url.search}${url.hash}`;
    } catch {
      sourceHref = mirroredFromSourceLink;
    }
  }

  const goToSourceAction = useCallback(() => {
    if (sourceHref) navigate(sourceHref);
  }, [navigate, sourceHref]);

  return { goToSourceAction, sourceHref, mirrorLang, canAccessSourceWorkspace };
}
